import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Truck } from 'lucide-react';
import { useFeatureVisibility } from '@/contexts/FeatureVisibilityContext';
import { useCurrency } from '@/hooks/useCurrency';
import { useWeightUnit } from '@/hooks/useWeightUnit';
import { CARD, Notice, OccupancyBar, PRIMARY_BUTTON } from '@/modules/supply/components/LoadEstimateParts';
import type { LoadEstimate } from '@/modules/supply/hooks/useLoadEstimate';
import type { LoadEstimateTruck } from '@/modules/supply/hooks/useLoadEstimateTruck';
import { useNumberFormats } from '@/modules/supply/hooks/useNumberFormats';
import { LOAD_ESTIMATE_KEY as T, containerCodeOf } from '@/modules/supply/utils/loadEstimateDraft';

/** Resumen de los proveedores marcados, consolidación en un solo contenedor y creación de la orden. */
export function LoadEstimateSummary({ estimate, truck }: { estimate: LoadEstimate; truck: LoadEstimateTruck }) {
  const { t } = useTranslation();
  const { number, decimal } = useNumberFormats();
  const { formatRounded } = useCurrency();
  const { unit: weightUnit, fromKg } = useWeightUnit();
  const { isRouteEnabled } = useFeatureVisibility();
  const canConfigureLogistics = isRouteEnabled('/settings/logistics', false);
  const {
    tableRows, marked, templates, saveStatus, creating, calculating, discardEstimate, createOrder, productConflicts,
  } = estimate;
  const { consolidate, setConsolidate, setConsolidateTemplateId, setColorBy, estimableMarked, consolidatedView } = truck;
  const supplierName = (supplierId: number) => tableRows.find((row) => row.supplier_id === supplierId)?.supplier_name ?? `#${supplierId}`;

  const markedRows = tableRows.filter((row) => marked.includes(row.supplier_id));
  const summary = markedRows.reduce(
    (total, row) => ({
      products: total.products + row.products,
      amount: total.amount + row.amount,
      boxes: total.boxes + row.boxes,
      pallets: total.pallets + row.pallets,
    }),
    { products: 0, amount: 0, boxes: 0, pallets: 0 },
  );
  // Volumen y peso de todos los marcados. Ocupación: con un contenedor común, frente a ese contenedor; si no, frente a la
  // suma de los contenedores de cada proveedor (con un solo proveedor es la misma cifra que la tabla).
  const measuredRows = markedRows.filter((row) => row.volume != null && row.weight != null);
  const markedVolume = measuredRows.reduce((total, row) => total + (row.volume ?? 0), 0);
  const markedWeight = measuredRows.reduce((total, row) => total + (row.weight ?? 0), 0);
  const markedCapacityVolume = measuredRows.reduce((total, row) => total + row.usableVolume, 0);
  const markedCapacityWeight = measuredRows.reduce((total, row) => total + row.usableWeight, 0);
  const markedVolumePct = consolidatedView
    ? consolidatedView.volumePct
    : markedCapacityVolume > 0 ? (markedVolume / markedCapacityVolume) * 100 : null;
  const markedWeightPct = consolidatedView
    ? consolidatedView.weightPct
    : markedCapacityWeight > 0 ? (markedWeight / markedCapacityWeight) * 100 : null;
  const unavailableMarked = markedRows.filter((row) => row.source?.unavailableReason);

  return (
      <section className={`${CARD} p-4`}>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-base font-semibold text-gray-900">{t(`${T}.summaryTitle`)}</h2>
          {marked.length > 0 ? (
            <button type="button" onClick={() => void discardEstimate()} className="text-sm font-medium text-gray-500 hover:text-red-700 hover:underline">
              {t(`${T}.discard`)}
            </button>
          ) : null}
        </div>
        {marked.length === 0 ? (
          <p className="text-sm text-gray-500">{t(`${T}.summaryEmpty`)}</p>
        ) : (
          <dl className="grid grid-cols-2 gap-3 sm:grid-cols-5">
            {([
              ['summarySuppliers', t(`${T}.summarySuppliers`), number.format(markedRows.length)],
              ['summaryProducts', t(`${T}.summaryProducts`), number.format(summary.products)],
              ['summaryAmount', t(`${T}.summaryAmount`), formatRounded(summary.amount)],
              ['summaryVolume', t(`${T}.columnVolume`), measuredRows.length > 0 ? `${decimal.format(markedVolume)} m³` : '—'],
              ['summaryWeight', t(`${T}.columnWeight`), measuredRows.length > 0 ? `${number.format(fromKg(markedWeight))} ${weightUnit}` : '—'],
              ['summaryBoxes', t(`${T}.summaryBoxes`), number.format(Math.round(summary.boxes))],
              ['summaryPallets', t(`${T}.summaryPallets`), number.format(summary.pallets)],
            ] as const).map(([key, label, value]) => (
              <div key={key} className="rounded-lg border border-gray-200 px-3 py-2">
                <dt className="text-xs text-gray-500">{label}</dt>
                <dd className="text-lg font-semibold tabular-nums text-gray-900">{value}</dd>
              </div>
            ))}
            <div className="rounded-lg border border-gray-200 px-3 py-2">
              <dt className="mb-1 text-xs text-gray-500">{t(`${T}.columnVolumePct`)}</dt>
              <dd><OccupancyBar pct={markedVolumePct} /></dd>
            </div>
            <div className="rounded-lg border border-gray-200 px-3 py-2">
              <dt className="mb-1 text-xs text-gray-500">{t(`${T}.columnWeightPct`)}</dt>
              <dd><OccupancyBar pct={markedWeightPct} /></dd>
            </div>
          </dl>
        )}
        {estimableMarked.length >= 2 ? (
          <div className="mt-3 rounded-lg border border-gray-200 px-3 py-2">
            <div className="flex flex-wrap items-center gap-3">
              <label className="inline-flex cursor-pointer items-center gap-2 text-sm font-medium text-gray-800">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300 text-[#1681ba] focus:ring-[#1681ba]"
                  checked={consolidate}
                  onChange={(event) => {
                    setConsolidate(event.target.checked);
                    if (event.target.checked) {
                      setColorBy('supplier');
                      setConsolidateTemplateId((current) => current ?? templates.find((item) => item.code === containerCodeOf(estimableMarked[0]))?.id ?? templates[0]?.id ?? null);
                    } else {
                      setColorBy('abc');
                    }
                  }}
                />
                {t(`${T}.consolidateLabel`)}
              </label>
              {consolidate ? (
                <label className="inline-flex items-center gap-2 text-xs text-gray-600">
                  {t(`${T}.consolidateContainer`)}
                  <select
                    value={consolidatedView?.template ? String(consolidatedView.template.id) : ''}
                    onChange={(event) => setConsolidateTemplateId(Number(event.target.value))}
                    className="max-w-52 rounded-md border border-gray-300 bg-white px-2 py-1 text-xs focus:border-[#1681ba] focus:outline-none focus:ring-1 focus:ring-[#1681ba]"
                  >
                    {templates.map((template) => (
                      <option key={template.id} value={template.id}>
                        {template.code}{template.usable_volume_m3 ? ` · ${number.format(template.usable_volume_m3)} m³` : ''}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}
            </div>
            {consolidatedView ? (
              <p
                role="status"
                className={`mt-2 text-xs font-medium ${!consolidatedView.known ? 'text-amber-700' : consolidatedView.fits ? 'text-green-700' : 'text-red-700'}`}
              >
                {consolidatedView.known
                  ? t(`${T}.${consolidatedView.fits ? 'consolidateFits' : 'consolidateNoFit'}`, {
                    volume: decimal.format(consolidatedView.volumePct ?? 0),
                    weight: decimal.format(consolidatedView.weightPct ?? 0),
                  })
                  : t(`${T}.consolidateUnknown`)}
                {unavailableMarked.length > 0 ? ` ${t(`${T}.consolidateExcluded`, { count: unavailableMarked.length })}` : ''}
              </p>
            ) : null}
          </div>
        ) : null}
        {unavailableMarked.length > 0 ? (
          <div className="mt-3 space-y-2">
            {unavailableMarked.map((row) => (
              <Notice key={row.supplier_id} tone="warning">
                <span>{t(`${T}.supplierUnavailable`, { supplier: row.supplier_name, reason: row.source?.unavailableReason })}</span>
                {canConfigureLogistics ? (
                  <Link to={`/settings/logistics/masters?supplier=${row.supplier_id}`} target="_blank" rel="noopener noreferrer" className="font-semibold underline">
                    {t(`${T}.configureSupplier`)}
                  </Link>
                ) : null}
              </Notice>
            ))}
          </div>
        ) : null}
        {productConflicts.length > 0 ? (
          <div className="mt-3 space-y-2">
            {productConflicts.map((conflict) => (
              <Notice key={conflict.productId} tone="error" role="alert">
                <span>
                  {t(`${T}.productConflict`, {
                    product: conflict.sku ? `${conflict.sku} · ${conflict.productName}` : conflict.productName,
                    suppliers: conflict.supplierIds.map(supplierName).join(', '),
                  })}
                </span>
              </Notice>
            ))}
          </div>
        ) : null}
        <div className="mt-4 flex flex-wrap items-center justify-end gap-3 border-t border-gray-100 pt-3">
          {saveStatus === 'idle' ? null : <span className="text-xs text-gray-500" role="status">{t(`${T}.${saveStatus === 'saving' ? 'saving' : 'saved'}`)}</span>}
          <button
            type="button"
            onClick={() => void createOrder()}
            disabled={creating || calculating || marked.length === 0 || productConflicts.length > 0}
            title={productConflicts.length > 0 ? t(`${T}.productConflictHint`) : t(`${T}.createOrderHint`)}
            className={PRIMARY_BUTTON}
          >
            <Truck className="h-4 w-4" aria-hidden="true" />
            {creating ? t(`${T}.creating`) : t(`${T}.createOrder`)}
          </button>
        </div>
      </section>
  );
}
